import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import MidenHubLogo from './MidenHub.png';
import { useWallet } from '../contexts/WalletContext';
import WalletSelectionModal from './WalletSelectionModal';

const Header = ({ currentPage, isSidebarCollapsed, setIsSidebarCollapsed }) => {
  const location = useLocation();
  const [showWalletDropdown, setShowWalletDropdown] = useState(false);
  const [showWalletSelection, setShowWalletSelection] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const { 
    connected, 
    publicKey, 
    connecting, 
    disconnecting,
    error, 
    disconnect, 
    wallet
  } = useWallet();

  const handleNavClick = () => {
    // Close mobile menu when navigating
    setIsMobileMenuOpen(false);
  };
  
  const getNavPath = (pageId) => {
    return pageId === 'home' ? '/' : `/${pageId}`;
  };
  
  const isActive = (pageId) => {
    if (pageId === 'home') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(`/${pageId}`);
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleWalletAction = () => {
    if (connected) {
      return;
    }
    
    // Show wallet selection modal
    setShowWalletSelection(true);
  };

  const copyAddress = async () => {
    if (publicKey) {
      try {
        await navigator.clipboard.writeText(publicKey.toString());
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to copy address:', err);
        }
      }
    }
    setShowWalletDropdown(false);
  };


  const handleDisconnect = async () => {
    await disconnect();
    setShowWalletDropdown(false);
  };

  const navItems = [
    {
      id: 'home',
      label: 'Home',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" strokeWidth="2"/>
          <polyline points="9,22 9,12 15,12 15,22" stroke="currentColor" strokeWidth="2"/>
        </svg>
      )
    },
    {
      id: 'playground',
      label: 'Playground',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M8 21v-4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v4" stroke="currentColor" strokeWidth="2"/>
        </svg>
      )
    },
    {
      id: 'achievements',
      label: 'Achievements',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" strokeWidth="2"/>
        </svg>
      )
    },
    {
      id: 'explore',
      label: 'Explore',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="currentColor" strokeWidth="2"/>
          <path d="M2 12h20" stroke="currentColor" strokeWidth="2"/>
        </svg>
      )
    },
    {
      id: 'glossary',
      label: 'Glossary',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <line x1="12" y1="17" x2="12.01" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    {
      id: 'ecosystem',
      label: 'Ecosystem',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" stroke="currentColor" strokeWidth="2"/>
        </svg>
      )
    }
  ];

  return (
    <>
      {/* Mobile Header */}
      <header className="mobile-header">
        <div className="mobile-header-content">
          <Link to="/" className="mobile-logo">
            <img src={MidenHubLogo} alt="Miden Hub" className="logo-image" />
            <span className="logo-text">Miden Hub</span>
          </Link>
          <div className="mobile-header-actions">
            <button className="mobile-menu-toggle" onClick={toggleMobileMenu}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M3 12h18M3 6h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay - SIMPLIFIED VERSION */}
      {isMobileMenuOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'flex-start'
          }}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <div 
            style={{
              width: '280px',
              height: '100vh',
              background: '#0f172a',
              borderRight: '1px solid #1e293b',
              padding: '20px',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px'}}>
              <div style={{display: 'flex', alignItems: 'center', gap: '0px'}}>
                <img src={MidenHubLogo} alt="Miden Hub" style={{width: '32px', height: '32px'}} />
                <span style={{color: 'white', fontSize: '20px', fontWeight: '700'}}>Miden Hub</span>
              </div>
              <button 
                onClick={() => setIsMobileMenuOpen(false)}
                style={{
                  background: 'transparent',
                  border: '1px solid #334155',
                  color: '#94a3b8',
                  padding: '8px',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
            </div>

            {/* Navigation Items */}
            <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
              {navItems.map((item) => (
                <Link
                  key={item.id}
                  to={getNavPath(item.id)}
                  onClick={handleNavClick}
                  style={{
                    background: isActive(item.id) ? 'rgba(249, 115, 22, 0.1)' : 'transparent',
                    color: isActive(item.id) ? '#f97316' : '#94a3b8',
                    border: `1px solid ${isActive(item.id) ? '#f97316' : '#334155'}`,
                    padding: '16px 20px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    textAlign: 'left',
                    fontSize: '16px',
                    fontWeight: '500',
                    textDecoration: 'none'
                  }}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </Link>
              ))}
            </div>

            {/* Wallet Section for Playground */}
            {currentPage === 'playground' && (
              <div style={{marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #1e293b'}}>
                {connected && publicKey ? (
                  <div>
                    <div style={{color: 'white', marginBottom: '10px'}}>
                      Wallet: {publicKey.toString().slice(0, 4)}...{publicKey.toString().slice(-4)}
                    </div>
                    <button 
                      onClick={handleDisconnect}
                      style={{
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        width: '100%'
                      }}
                    >
                      Disconnect Wallet
                    </button>
                  </div>
                ) : (
                  <button 
                    onClick={handleWalletAction}
                    style={{
                      background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      width: '100%',
                      fontWeight: '600'
                    }}
                  >
                    Connect Wallet
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className={`main-sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <Link to="/" className="sidebar-logo">
            <div className="logo-icon">
              <img src={MidenHubLogo} alt="Miden Hub" className="logo-image" />
            </div>
            {!isSidebarCollapsed && <span className="logo-text">Miden Hub</span>}
          </Link>
          <div className="sidebar-header-actions">
            <button className="sidebar-toggle" onClick={toggleSidebar}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </button>
          </div>
        </div>

        {currentPage === 'playground' && (
          <div className="wallet-section">
            {connected && publicKey ? (
              <div className="wallet-dropdown-container">
                <button 
                  className="wallet-account-button"
                  onClick={() => setShowWalletDropdown(!showWalletDropdown)}
                >
                  <div className="wallet-account-display">
                    <div className="miden-logo" style={{width: '16px', height: '16px'}}>
                      <img 
                        src="data:image/png;base64,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" 
                        alt="Miden Wallet"
                        width={16}
                        height={16}
                        style={{ display: 'block' }}
                      />
                    </div>
                    {!isSidebarCollapsed && (
                      <>
                        <span className="account-address">
                          {publicKey.toString().slice(0, 4)}...{publicKey.toString().slice(-4)}
                        </span>
                        <svg 
                          width="12" 
                          height="12" 
                          viewBox="0 0 24 24" 
                          fill="none"
                          className={`dropdown-arrow ${showWalletDropdown ? 'open' : ''}`}
                        >
                          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                      </>
                    )}
                  </div>
                </button>

                {showWalletDropdown && !isSidebarCollapsed && (
                  <div className="wallet-dropdown-menu">
                    <button 
                      className="wallet-dropdown-item"
                      onClick={copyAddress}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      Copy Address
                    </button>
                    <button 
                      className="wallet-dropdown-item disconnect"
                      onClick={handleDisconnect}
                      disabled={disconnecting}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" strokeWidth="2"/>
                        <polyline points="16 17 21 12 16 7" stroke="currentColor" strokeWidth="2"/>
                        <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      {disconnecting ? 'Disconnecting...' : 'Disconnect Wallet'}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <button 
                  className={`connect-wallet-button ${connecting ? 'loading' : ''}`}
                  onClick={handleWalletAction}
                  disabled={connecting}
                >
                  {connecting ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{marginRight: '8px'}}>
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.3"/>
                      <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <animateTransform attributeName="transform" type="rotate" dur="1s" repeatCount="indefinite" values="0 12 12;360 12 12"/>
                      </path>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{marginRight: '8px'}}>
                      <path d="M21 12c0 1.2-4.03 2-9 2s-9-.8-9-2 4.03-2 9-2 9 .8 9 2z" stroke="currentColor" strokeWidth="2"/>
                      <path d="M3 5v14c0 1.2 4.03 2 9 2s9-.8 9-2V5" stroke="currentColor" strokeWidth="2"/>
                      <path d="M3 12v7c0 1.2 4.03 2 9 2s9-.8 9-2v-7" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  )}
                  {!isSidebarCollapsed && (
                    connecting ? "Connecting..." : 
                    wallet ? "Connect Wallet" : "Install Wallet"
                  )}
                </button>
                
                {error && !isSidebarCollapsed && (
                  <div className="wallet-error">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{marginRight: '4px'}}>
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                      <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2"/>
                      <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    {error}
                  </div>
                )}

                {!wallet && !connected && !isSidebarCollapsed && (
                  <div className="wallet-status">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{marginRight: '4px'}}>
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                      <path d="M12 8v4" stroke="currentColor" strokeWidth="2"/>
                      <path d="M12 16h.01" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    Install Miden Wallet extension to connect
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {currentPage === 'playground' && <div className="sidebar-divider"></div>}
      
      <nav className="sidebar-nav">
        {navItems.map((item) => (
          <Link
            key={item.id}
            to={getNavPath(item.id)}
            className={`sidebar-nav-item ${isActive(item.id) ? 'active' : ''}`}
            title={isSidebarCollapsed ? item.label : ''}
          >
            <span className="nav-icon">{item.icon}</span>
            {!isSidebarCollapsed && <span className="nav-label">{item.label}</span>}
          </Link>
        ))}
      </nav>
        
        {showWalletSelection && (
          <WalletSelectionModal onClose={() => setShowWalletSelection(false)} />
        )}
      </aside>
    </>
  );
};

export default Header;